import _                                                                                  from 'underscore';
import classNames                                                                         from 'classnames';
import { ClassValue }                                                                     from 'classnames/types';
import React, { CSSProperties, FC, MutableRefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { Sidebar, SidebarProps }                                                          from 'semantic-ui-react';

import { SystemEvent }                                                 from '../../types';
import { runIfFunction }                                               from '../../util';
import { useFeatureFlags, useKeyPress, useSidebar, useUIDisplayModes } from '../../hooks';
import { TogglableContentHook }                                        from '../../hooks/useTogglableContentReducer';

export interface SidebarComponentProps extends SidebarProps {
	sidebar?: TogglableContentHook<SidebarProps>,
	dimmed?: boolean,
	offsetTop?: number,
	offsetBottom?: number,
	contentClassName?: ClassValue,
	contentStyle?: CSSProperties,
	wrapperStyle?: CSSProperties,
	ref?: MutableRefObject<[
		containerEl: React.Ref<any>,
		sidebarHook: TogglableContentHook<SidebarProps>[0],
		sidebarHookActions: TogglableContentHook<SidebarProps>[1]
	]>,

	onAfterLayout?: ( ref: React.Ref<any> ) => void,

	hideVerticalContentOverflow?: boolean,
}

let defaultProps: SidebarComponentProps = {
	sidebar:      null,
	offsetTop:    null,
	offsetBottom: null,
	onHide:       null,

	animation: 'overlay',
	direction: 'right',
	width:     'wide',
	dimmed:    false,

	componentRef:     null,
	contentClassName: null,
	contentStyle:     null,
	wrapperStyle:     null,

	onAfterLayout: null,

	hideVerticalContentOverflow: false,
};

const SidebarComponent: FC<SidebarComponentProps> = ( props ) => {

	let [ sidebar, sidebarActions ] = props.sidebar || useSidebar(),
	    semanticUISidebarProps      = sidebar.props as SidebarProps,
	    escKeyPressed               = useKeyPress( "Escape" ),
	    containerRef                = useRef<any>(),
	    featureFlags                = useFeatureFlags(),
	    { minimizedChrome }         = useUIDisplayModes();

	// ...when features change
	useEffect( () => {}, [ featureFlags ] );

	useEffect( () => {

		runLayoutEffects()

	}, [ props.offsetTop, props.offsetBottom, minimizedChrome ] );

	// when esc key is pressed...
	useEffect( () => {

		if( escKeyPressed && sidebar.isOpen ){

			handleHideSidebar();
		}

	}, [ escKeyPressed ] );

	// on layout...
	useLayoutEffect( () => {

		reattachContainerReference();

		runLayoutEffects();
	} )

	// effects that alter the Semantic UI container,
	// or address relayout issues from content.
	let runLayoutEffects = () => {

		sizeContainerHeight();

		determineOverflowScheme();

		scrollToTop();
	}

	let reattachContainerReference = () => {

		let refData = [ containerRef.current.ref.current, sidebar, sidebarActions ];

		if( sidebar.props.componentRef ){
			sidebar.props.componentRef.current = refData;

		}

		if( props.componentRef ){
			props.componentRef.current = refData;
		}
	}

	// Whether to remove the overflow scheme on the SUI container or not
	let determineOverflowScheme = () => {

		if( containerRef.current ){

			if( props.hideVerticalContentOverflow ){

				containerRef.current.ref.current.style.setProperty( 'overflow-y', `hidden`, 'important' );
			}
		}
	}

	// Determine proper container fit when an offset is specified
	let sizeContainerHeight = () => {

		if( containerRef.current ){

			if( ( props.offsetTop || props.offsetBottom ) ){

				// move the actual sidebar down
				if( props.direction === 'right' || props.direction === 'left' ){

					containerRef.current.ref.current.style.setProperty( 'top', `${ props.offsetTop || 0 }px` );
					containerRef.current.ref.current.style.setProperty( 'height', `calc( 100% - ${ ( props.offsetTop || 0 ) + ( props.offsetBottom || 0 ) }px )`, 'important' );

				}
			}
		}
	}

	// Scroll content up when re layout occurs
	let scrollToTop = () => {

		if( containerRef.current ){

			containerRef.current.ref.current.scrollTo( 0, 0 );
		}
	}

	let handleHideSidebar = ( e?: SystemEvent, data?: SidebarProps ) => {

		sidebarActions.toggle( false )

		let { onHide = null } = ( sidebar.props as SidebarProps );

		runIfFunction( onHide, e as any, data );
	};

	let omittedProps = [
		'sidebar',
		'dimmed',
		'contentClassName',
		'contentStyle',
		'wrapperStyle',
		'componentRef',
		'offsetTop',
		'offsetBottom',
		'onAfterLayout',
		'hideVerticalContentOverflow',
	]

	let sidebarProps: SidebarProps = {
		..._.omit.apply( this, [ props, ...omittedProps ] ),
		..._.omit( sidebar.props, 'dimmed' ),
		className: 'container--colored-bg-lunar',
		onHide:    handleHideSidebar,
	};

	let contentClasses = classNames(
		{ 'position--relative': props.offset },
		sidebarProps.className,
		props.contentClassName
	);

	return (

		<Sidebar.Pushable>

			<Sidebar ref={ containerRef }
			         { ...sidebarProps }
			         className={ contentClasses }
			         style={ props.contentStyle }
			         visible={ sidebar.isOpen }>

				{/* Sidebar Content */ }
				{ sidebar.content }

			</Sidebar>
			<Sidebar.Pusher dimmed={ props.dimmed && sidebar.isOpen } style={ props.wrapperStyle }>

				{ props.children }

			</Sidebar.Pusher>

		</Sidebar.Pushable>
	);
};

SidebarComponent.defaultProps = defaultProps;

export { SidebarComponent }
